import { Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import { FluidModule } from 'primeng/fluid';
import { Subscription } from 'rxjs/internal/Subscription';
import { LayoutService } from '@/layout/service/layout.service';
import { debounceTime } from 'rxjs';
import { AStatsWidget } from './components/a-statswidget';
import { AAiSummaryWidget } from './components/a-ai-summary-widget';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectComponent } from '@/shared/ui-components/select/select.component';
import { Option } from '@/shared/enums/generic.enum';
import { FormsModule } from '@angular/forms';
import { LanguageService } from '@/shared/services/language.service';
import { filterPeriodValues, DEFAULT_FILTER_PERIOD_VALUE, FILTER_PERIOD_ALL_VALUE } from '@/shared/data/generic';
import { BestSellingWidget } from '@/pages/dummy-templates/dashboard/components/bestsellingwidget/bestsellingwidget';
import { NotificationsWidget } from '@/pages/dummy-templates/dashboard/components/notificationswidget/notificationswidget';
import { RevenueStreamWidget } from '@/pages/dummy-templates/dashboard/components/revenuestreamwidget/revenuestreamwidget';
import { DoughnutChartComponent } from '@/shared/ui-components/charts/doughnut-chart/doughnut-chart.component';
import { LineChartComponent } from '@/shared/ui-components/charts/line-chart/line-chart.component';
import { BarChartComponent } from '@/shared/ui-components/charts/bar-chart/bar-chart.component';
import { CardComponent } from '@/shared/ui-components/card/card.component';
import { ContentHeaderComponent } from '@/shared/components/content-header/content-header.component';
import { AdminDashboardService } from './admin-dashboard.service';
import { AdminTrendDto, wmOnboardingSliceLabel } from './data-access/admin-dashboard.model';
import { SkeletonModule } from 'primeng/skeleton';
import { GenericProfileService } from '@/shared/services/profile.service';
import { UserProfileService } from '@/pages/user-profile/user-profile.service';
import { Role } from '@/core/models/auth.interface';
import { AI_FEATURE_ENABLED } from '@/shared/utilities/feature-flags';
@Component({
    selector: 'app-admin-dashboard',
    standalone: true,
    imports: [FormsModule, TranslateModule, BestSellingWidget, RevenueStreamWidget, NotificationsWidget, ChartModule, FluidModule, AStatsWidget, AAiSummaryWidget, SelectComponent,
        LineChartComponent, BarChartComponent, DoughnutChartComponent, CardComponent, ContentHeaderComponent, SkeletonModule],
    templateUrl: './admin-dashboard.component.html',
    styles: [`
        :host {
          flex: 1 1 0%;
        }
      `]
})
export class AdminDashboardComponent implements OnInit, OnDestroy {
    protected readonly aiFeatureEnabled = AI_FEATURE_ENABLED;
    lineData: any;
    barData: any = { labels: [], datasets: [] };
    pieData: any;
    polarData: any;
    radarData: any;
    lineOptions: any;
    barOptions: any;
    pieOptions: any;
    doughnutChartData: any = { labels: [], datasets: [{ data: [], backgroundColor: [], hoverBackgroundColor: [] }] };
    doughnutChartOptions: any;
    polarOptions: any;
    radarOptions: any;

    public filterPeriodValues: Option<string>[] = filterPeriodValues;
    public filterPeriodValue: string = DEFAULT_FILTER_PERIOD_VALUE;
    public readonly filterPeriodValueReady = signal(false);

    private readonly configUpdateSub: Subscription;

    private readonly _layoutService = inject(LayoutService);
    private readonly _translateService = inject(TranslateService);
    private readonly _languageService = inject(LanguageService);
    private readonly _adminDashboardService = inject(AdminDashboardService);
    private readonly _genericProfileService = inject(GenericProfileService);
    private readonly _userProfileService = inject(UserProfileService);

    /** Admin roles that should only see deal-related stats/widgets on dashboard. */
    public readonly isDealsOnlyAdmin = signal<boolean>(false);

    /** Exposed for template: WM onboarding chart loading/error state. */
    public readonly wmOnboardingStatusState = this._adminDashboardService.wmOnboardingStatusState;
    /** Exposed for template: Applicant types bar chart loading/error state. */
    public readonly applicantTypesState = this._adminDashboardService.applicantTypesState;
    /** Exposed for template/logic: Investments trend state. */
    public readonly trendState = this._adminDashboardService.trendState;
    public readonly volumeByAssetClassState = this._adminDashboardService.volumeByAssetClassState;
    public readonly revenueByProductTypeState = this._adminDashboardService.revenueByProductTypeState;

    revenueStreamChartData: any = { labels: [], datasets: [] };
    revenueStreamChartOptions: any;

    /** Header text tracks profile signal and language readiness for translations. */
    public readonly dashboardHeaderTitle = computed(() => {
        this._languageService.ready();
        const profile = this._userProfileService.userProfileData();
        const baseTitle = this._translateService.instant('ADMIN.DASHBOARD.HEADER') as string;
        const fullName = `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim() || profile?.name || '';
        const roleLabel = profile?.roleLabel ? (this._translateService.instant(profile.roleLabel) as string) : '';
        return [baseTitle, fullName, roleLabel].filter(Boolean).join(' - ');
    });

    private _suppressNextTrendSelectChange = true;
    private _defaultPeriodApplied = false;

    constructor() {
        const role = this._genericProfileService.genericProfile()?.role;
        this.isDealsOnlyAdmin.set(
            role === Role.ADMIN_DEAL_USER_PREPARER ||
            role === Role.ADMIN_DEAL_USER_APPROVER ||
            role === Role.ADMIN_CLIENT_USERS
        );

        this.configUpdateSub = this._layoutService.configUpdate$.pipe(debounceTime(25)).subscribe(() => {
            this.initCharts();
        });

        effect(() => {
            if (this._languageService.ready()) {
                this.filterPeriodValues = this.filterPeriodValues.map((item) => ({
                    ...item,
                    label: this._translateService.instant(item.label)
                }));

                this.filterPeriodValueReady.set(true);
            }
        });

        effect(() => {
            this._adminDashboardService.wmOnboardingStatusState();
            this.buildWmOnboardingDoughnutChart();
        });

        effect(() => {
            this._adminDashboardService.applicantTypesState();
            this.buildApplicantTypesBarChart();
        });

        effect(() => {
            this._adminDashboardService.trendState();
            this.buildInvestmentsLineChart();
        });

        effect(() => {
            this._adminDashboardService.revenueByProductTypeState();
            this.buildRevenueStreamChart();
        });
    }

    public ngOnInit() {
        this._userProfileService.getUserProfile();
        // Apply the default All filter on initial load.
        // This keeps all widgets aligned with the default select value.
        if (!this._defaultPeriodApplied) {
            this._defaultPeriodApplied = true;
            this.applyDashboardPeriodFilter(this.filterPeriodValue);
        }
        this.initCharts();
    }

    public onSelectFilterPeriodValue(event?: unknown): void {
        const nextValue =
            (event && typeof event === 'object' && 'value' in (event as any)) ? String((event as any).value) :
                (event != null ? String(event) : this.filterPeriodValue);

        // If the select fires an initial change event with the default value, avoid double-hitting the API.
        if (this._suppressNextTrendSelectChange && nextValue === this.filterPeriodValue) {
            this._suppressNextTrendSelectChange = false;
            return;
        }
        this._suppressNextTrendSelectChange = false;

        // If the control fires a change event but the value didn't change, ignore it.
        if (nextValue === this.filterPeriodValue) return;

        this.filterPeriodValue = nextValue;

        this.applyDashboardPeriodFilter(nextValue);
    }

    private applyDashboardPeriodFilter(periodValue: string): void {
        if (periodValue === FILTER_PERIOD_ALL_VALUE) {
            this._adminDashboardService.getAdminTrend({ view: 0, groupBy: 'month' }).subscribe();
            this._adminDashboardService.getWmOnboardingStatus().subscribe();
            this._adminDashboardService.getApplicantTypes().subscribe();
            this._adminDashboardService.getVolumeByAssetClass().subscribe();
            this._adminDashboardService.getRevenueByProductType().subscribe();
            return;
        }

        const days = Number(periodValue);
        if (!Number.isFinite(days) || days <= 0) {
            this._adminDashboardService.getAdminTrend({ view: 0, groupBy: 'month' }).subscribe();
            this._adminDashboardService.getWmOnboardingStatus().subscribe();
            this._adminDashboardService.getApplicantTypes().subscribe();
            this._adminDashboardService.getVolumeByAssetClass().subscribe();
            this._adminDashboardService.getRevenueByProductType().subscribe();
            return;
        }

        const now = new Date();
        const from = new Date(now);
        from.setUTCDate(from.getUTCDate() - days);
        const fromUtc = from.toISOString();
        const toUtc = now.toISOString();

        // Heuristic: short ranges => day grouping, else month grouping.
        const groupBy = days <= 30 ? 'day' : 'month';

        this._adminDashboardService.getAdminTrend({ view: 0, fromUtc, toUtc, groupBy }).subscribe();
        this._adminDashboardService.getWmOnboardingStatus({ fromUtc, toUtc }).subscribe();
        this._adminDashboardService.getApplicantTypes({ fromUtc, toUtc }).subscribe();
        this._adminDashboardService.getVolumeByAssetClass({ fromUtc, toUtc }).subscribe();
        this._adminDashboardService.getRevenueByProductType({ fromUtc, toUtc }).subscribe();
    }

    public volumeByAssetClassIconByKey(key: string | null | undefined): string {
        const k = String(key ?? '').trim();
        switch (k) {
            case 'PrivateEquity':
                return './icons/private_equity.svg';
            case 'PrivateCredit':
                return './icons/private_credit.svg';
            case 'DigitalAssets':
                return './icons/digital_assets.svg';
            case 'RealEstate':
                return './icons/real_estate.svg';
            case 'Commodities':
                return './icons/commodities.svg';
            case 'Collectibles':
                return './icons/collectibles.svg';
            default:
                return './icons/private_equity.svg';
        }
    }

    private buildWmOnboardingDoughnutChart(documentStyle?: CSSStyleDeclaration): void {
        const style = documentStyle ?? getComputedStyle(document.documentElement);
        const state = this._adminDashboardService.wmOnboardingStatusState();
        const slices = state.data?.slices ?? [];

        const labels = slices.map((s) => wmOnboardingSliceLabel(s.category));
        const data = slices.map((s) => s.value);
        const colorVars = [
            '--lime-500',
            '--deepteal-500',
            '--lime-700',
            '--deepteal-400',
            '--deepteal-200',
            '--deepteal-50',
            '--deepteal-700',
        ];
        const backgroundColor = labels.map((_, i) => {
            const v = colorVars[i % colorVars.length];
            return style.getPropertyValue(v).trim() || v;
        });
        const hoverBackgroundColor = [...backgroundColor];

        this.doughnutChartData = {
            labels,
            datasets: [
                {
                    data,
                    backgroundColor,
                    hoverBackgroundColor,
                },
            ],
        };
    }

    private buildApplicantTypesBarChart(documentStyle?: CSSStyleDeclaration): void {
        const style = documentStyle ?? getComputedStyle(document.documentElement);
        const state = this._adminDashboardService.applicantTypesState();
        const res = state.data;
        if (!res?.labels?.length || !res?.series || !Object.keys(res.series).length) {
            this.barData = { labels: [], datasets: [] };
            return;
        }
        const colorVars = [
            '--deepteal-600',
            '--deepteal-500',
            '--deepteal-400',
            '--deepteal-200',
            '--deepteal-50',
            '--lime-500',
        ];
        const datasets = Object.entries(res.series).map(([label, data], i) => {
            const color = style.getPropertyValue(colorVars[i % colorVars.length]).trim() || colorVars[i % colorVars.length];
            return {
                label,
                backgroundColor: color,
                borderColor: color,
                data,
                categoryPercentage: 0.8,
                barPercentage: 1.0,
            };
        });
        this.barData = {
            labels: res.labels,
            datasets,
        };
    }

    private initCharts(): void {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--text-color');
        const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
        const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

        this.buildApplicantTypesBarChart(documentStyle);

        

        this.pieData = {
            labels: ['Draft', 'Submitted', 'Under review', 'Approved', 'Rejected'],
            datasets: [
                {
                    data: [540, 325, 702, 1509, 10],
                    backgroundColor: [
                        documentStyle.getPropertyValue('--p-indigo-500'),
                        documentStyle.getPropertyValue('--p-indigo-700'),
                        documentStyle.getPropertyValue('--p-purple-500'),
                        documentStyle.getPropertyValue('--p-teal-500'),
                        documentStyle.getPropertyValue('--p-teal-700')
                    ],
                    hoverBackgroundColor: [
                        documentStyle.getPropertyValue('--p-indigo-400'),
                        documentStyle.getPropertyValue('--p-indigo-600'),
                        documentStyle.getPropertyValue('--p-purple-400'),
                        documentStyle.getPropertyValue('--p-teal-400'),
                        documentStyle.getPropertyValue('--p-teal-600')
                    ]
                }
            ]
        };

        this.pieOptions = {
            plugins: {
                legend: {
                    labels: {
                        usePointStyle: true,
                        color: textColor
                    }
                }
            }
        };
        this.buildWmOnboardingDoughnutChart(documentStyle);

        this.buildInvestmentsLineChart(documentStyle);
        this.buildRevenueStreamChart(documentStyle);
        this.buildRevenueStreamChartOptions(documentStyle);

        // this.lineOptions = {
        //     maintainAspectRatio: false,
        //     aspectRatio: 0.8,
        //     plugins: {
        //         legend: {
        //             position: 'bottom',
        //             labels: {
        //                 color: textColor
        //             }
        //         }
        //     },
        //     scales: {
        //         x: {
        //             ticks: {
        //                 color: textColorSecondary
        //             },
        //             grid: {
        //                 color: surfaceBorder,
        //                 drawBorder: false
        //             }
        //         },
        //         y: {
        //             ticks: {
        //                 color: textColorSecondary
        //             },
        //             grid: {
        //                 color: surfaceBorder,
        //                 drawBorder: false
        //             }
        //         }
        //     }
        // };

        this.polarData = {
            datasets: [
                {
                    data: [11, 16, 7, 3],
                    backgroundColor: [documentStyle.getPropertyValue('--p-indigo-500'), documentStyle.getPropertyValue('--p-purple-500'), documentStyle.getPropertyValue('--p-teal-500'), documentStyle.getPropertyValue('--p-orange-500')],
                    label: 'My dataset'
                }
            ],
            labels: ['Indigo', 'Purple', 'Teal', 'Orange']
        };

        this.polarOptions = {
            plugins: {
                legend: {
                    labels: {
                        color: textColor
                    }
                }
            },
            scales: {
                r: {
                    grid: {
                        color: surfaceBorder
                    },
                    ticks: {
                        display: false,
                        color: textColorSecondary
                    }
                }
            }
        };

        this.radarData = {
            labels: ['Eating', 'Drinking', 'Sleeping', 'Designing', 'Coding', 'Cycling', 'Running'],
            datasets: [
                {
                    label: 'My First dataset',
                    borderColor: documentStyle.getPropertyValue('--p-indigo-400'),
                    pointBackgroundColor: documentStyle.getPropertyValue('--p-indigo-400'),
                    pointBorderColor: documentStyle.getPropertyValue('--p-indigo-400'),
                    pointHoverBackgroundColor: textColor,
                    pointHoverBorderColor: documentStyle.getPropertyValue('--p-indigo-400'),
                    data: [65, 59, 90, 81, 56, 55, 40]
                },
                {
                    label: 'My Second dataset',
                    borderColor: documentStyle.getPropertyValue('--p-purple-400'),
                    pointBackgroundColor: documentStyle.getPropertyValue('--p-purple-400'),
                    pointBorderColor: documentStyle.getPropertyValue('--p-purple-400'),
                    pointHoverBackgroundColor: textColor,
                    pointHoverBorderColor: documentStyle.getPropertyValue('--p-purple-400'),
                    data: [28, 48, 40, 19, 96, 27, 100]
                }
            ]
        };

        this.radarOptions = {
            plugins: {
                legend: {
                    labels: {
                        color: textColor
                    }
                }
            },
            scales: {
                r: {
                    pointLabels: {
                        color: textColor
                    },
                    grid: {
                        color: surfaceBorder
                    }
                }
            }
        };
    }

    private buildInvestmentsLineChart(documentStyle?: CSSStyleDeclaration): void {
        const style = documentStyle ?? getComputedStyle(document.documentElement);
        const state = this._adminDashboardService.trendState();
        const res: AdminTrendDto | null = state.data ?? null;
        if (!res?.categories?.length || !res?.series?.length) {
            this.lineData = { labels: [], datasets: [] };
            return;
        }

        const labels = res.categories;
        const colorVars = [
            '--deepteal-500',
            '--charcoal-450',
            '--deepteal-700',
            '--lime-500',
            '--deepteal-200',
        ];
        const datasets = (res.series ?? []).map((s, i) => {
            const color = style.getPropertyValue(colorVars[i % colorVars.length]).trim() || colorVars[i % colorVars.length];
            const valuesByLabel = new Map((s.data ?? []).map((p) => [p.label, p.value]));
            return {
                label: s.name ?? s.key ?? `Series ${i + 1}`,
                data: labels.map((l) => valuesByLabel.get(l) ?? 0),
                fill: false,
                backgroundColor: color,
                borderColor: color,
                tension: 0.4,
            };
        });

        this.lineData = { labels, datasets };
    }

    private buildRevenueStreamChart(documentStyle?: CSSStyleDeclaration): void {
        const style = documentStyle ?? getComputedStyle(document.documentElement);
        const state = this._adminDashboardService.revenueByProductTypeState();
        const res = state.data;
        if (!res?.categories?.length || !res?.series?.length) {
            this.revenueStreamChartData = { labels: [], datasets: [] };
            return;
        }

        const labels = res.categories;
        const colorVars = [
            '--deepteal-500',
            '--deepteal-400',
            '--deepteal-200',
            '--deepteal-100',
            '--deepteal-600',
            '--lime-500',
        ];
        const datasets = (res.series ?? []).map((s, i) => {
            const color = style.getPropertyValue(colorVars[i % colorVars.length]).trim() || colorVars[i % colorVars.length];
            const valuesByLabel = new Map((s.data ?? []).map((p) => [p.label, p.value]));
            return {
                type: 'bar',
                label: s.name ?? s.key ?? `Series ${i + 1}`,
                backgroundColor: color,
                data: labels.map((l) => valuesByLabel.get(l) ?? 0),
                borderSkipped: false,
                barThickness: 32,
            };
        });

        this.revenueStreamChartData = {
            labels,
            datasets,
        };
    }

    private buildRevenueStreamChartOptions(documentStyle?: CSSStyleDeclaration): void {
        const style = documentStyle ?? getComputedStyle(document.documentElement);
        const textColor = style.getPropertyValue('--text-color');

        this.revenueStreamChartOptions = {
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        padding: 30,
                        boxWidth: 10,
                        boxHeight: 10,
                        color: textColor,
                        font: {
                            size: 15,
                            weight: '500',
                        },
                    },
                },
            },
            scales: {
                x: {
                    stacked: true,
                    grid: {
                        color: 'transparent',
                        borderColor: 'transparent',
                    },
                },
                y: {
                    stacked: true,
                    ticks: {
                        font: {
                            size: 12,
                            weight: '500',
                            family: 'Inter, Arial, sans-serif',
                        },
                        padding: 20,
                    },
                    grid: {
                        borderColor: 'transparent',
                        drawTicks: false,
                        tickLength: 0,
                    },
                },
            },
        };
    }

    public ngOnDestroy() {
        this.configUpdateSub.unsubscribe();
    }
}
